export const PortableGameConfigSchemaVersion = 'game-config.v1' as const;

export type PortableGameConfigSchemaVersion = typeof PortableGameConfigSchemaVersion;

export type PortableThemeName = 'dark' | 'dark-blue' | 'gray-black';

export type PortableGamePlayRulePreference = 1 | 2;

export interface PortableGameConfig {
  schemaVersion: PortableGameConfigSchemaVersion;
  metadata: PortableGameConfigMetadata;
  event: PortableEventConfig;
  teams: [PortableTeamConfig, PortableTeamConfig];
  rounds: PortableRoundConfig[];
  gamePreferences: PortableGamePreferencesConfig;
  theme: PortableThemeConfig;
}

export interface PortableThemeConfig {
  name: PortableThemeName | string;
  key?: string;
  media: PortableMediaConfig;
  visuals: PortableThemeVisuals;
  'theme-variables'?: Record<string, string>;
  Team1?: PortableDisplayScope;
  Team2?: PortableDisplayScope;
  Projector?: PortableDisplayScope;
  EORSummary?: PortableDisplayScope;
  EOGSummary?: PortableDisplayScope;
}

export interface PortableThemeVisuals {
  backgrounds: {
    team?: PortableMediaReference | null;
    team1?: PortableMediaReference | null;
    team2?: PortableMediaReference | null;
    projector?: PortableMediaReference | null;
    roundSummary?: PortableMediaReference | null;
    endOfGameSummary?: PortableMediaReference | null;
  };
  blocks: Record<PortableThemeVisualBlockKey, PortableThemeVisualBlock>;
}

export interface PortableDisplayScope {
  sections?: Record<string, {
    objects: Record<string, PortableDisplayObject>;
  }>;
}

export interface PortableDisplayObject {
  values: PortableDisplayValues;
  selected?: PortableDisplayValues | null;
  unselected?: PortableDisplayValues | null;
}

export interface PortableDisplayValues {
  backgroundColor?: string | null;
  imageOpacityPercent?: number | null;
  border?: {
    color?: string | null;
    cornerStyle?: 'Square' | 'Rounded' | 'Pill' | null;
    thicknessPx?: number | null;
  } | null;
  font?: {
    color?: string | null;
    sizeRem?: number | null;
  } | null;
  svg?: {
    source?: string | null;
    sizePx?: number | null;
    direction?: 'Horizontal' | 'Vertical' | null;
    rotationDegrees?: number | null;
    color?: string | null;
  } | null;
}

export type PortableThemeVisualBlockKey =
  | 'team1Left'
  | 'team1Right'
  | 'team2Left'
  | 'team2Right'
  | 'projectorLeft'
  | 'projectorRight'
  | 'bodyMain';

export interface PortableThemeVisualBlock {
  name: string;
  image: PortableMediaReference | null;
  svg: PortableMediaReference | null;
}

export interface PortableGameConfigMetadata {
  exportedAtUtc: string;
  exportedByUserId?: number;
  source: 'Portal';
  sourceVersion?: string;
}

export interface PortableEventConfig {
  id: number;
  organizationId: number;
  name: string;
  description?: string;
  scheduledDateTime?: string;
  numberOfRounds: number;
}

export interface PortableTeamConfig {
  teamNumber: 1 | 2;
  id?: number;
  name: string;
  members: PortableTeamMemberConfig[];
}

export interface PortableTeamMemberConfig {
  orderNumber: number;
  firstName?: string;
  lastName?: string;
  displayName: string;
}

export interface PortableRoundConfig {
  roundNumber: number;
  question: PortableQuestionConfig;
}

export interface PortableQuestionConfig {
  id?: number;
  text: string;
  answers: PortableAnswerConfig[];
}

export interface PortableAnswerConfig {
  orderNumber: number;
  text: string;
  points: number;
}

export interface PortableGamePreferencesConfig {
  gamePlayRulePreference: PortableGamePlayRulePreference;
  maxWrongAttempts: number;
  WinnerTimerValue?: number;
  timers?: PortableTimersConfig;
}

export interface PortableTimersConfig {
  podium: PortableTimerSettingConfig;
  answer: PortableTimerSettingConfig;
}

export interface PortableTimerSettingConfig {
  enabled: boolean;
  seconds: number;
}

export interface PortableMediaConfig {
  rightAnswer?: PortableMediaReference[];
  wrongAnswer?: PortableMediaReference[];
  endOfRound?: PortableMediaReference[];
  nextTeamTurn?: PortableMediaReference[];
  nextRound?: PortableMediaReference[];
  intro?: PortableMediaReference[];
  break?: PortableMediaReference[];
  buzzer?: PortableMediaReference[];
  suddenDeath?: PortableMediaReference[];
  endOfGame?: PortableMediaReference[];
  threeThreeAndOut?: PortableMediaReference[];
  threeThreeSuddenDeath?: PortableMediaReference[];
  headToHead?: PortableMediaReference[];
  penaltyShot?: PortableMediaReference[];
}

export interface PortableMediaReference {
  id?: number;
  artifactId?: number;
  fileName: string;
  contentType?: string;
  blobPath?: string;
  url?: string;
}
